import { useEffect, useState } from 'react';
import { NavLink, Route, Routes, useNavigate, Navigate } from 'react-router-dom';
import {
  Star, GraduationCap, BarChart3, Users, HelpCircle,
  Mail, Settings, LogOut, ExternalLink, Loader2, Inbox, MessageSquareText,
} from 'lucide-react';
import { adminMe, adminLogout } from '../../lib/adminApi';
import ReviewsSection from './sections/ReviewsSection';
import CoursesSection from './sections/CoursesSection';
import ResultsSection from './sections/ResultsSection';
import TeamSection from './sections/TeamSection';
import FaqsSection from './sections/FaqsSection';
import ContactSection from './sections/ContactSection';
import SettingsSection from './sections/SettingsSection';
import EnquiriesSection from './sections/EnquiriesSection';
import WhatsAppTestimonialsSection from './sections/WhatsAppTestimonialsSection';

const NAV = [
  { to: '/admin/enquiries',    label: 'Enquiries',           icon: Inbox },
  { to: '/admin/reviews',      label: 'Reviews',             icon: Star },
  { to: '/admin/testimonials', label: 'WhatsApp messages',   icon: MessageSquareText },
  { to: '/admin/courses',      label: 'Courses & Pricing',   icon: GraduationCap },
  { to: '/admin/results',      label: 'Results',             icon: BarChart3 },
  { to: '/admin/team',         label: 'Team',                icon: Users },
  { to: '/admin/faqs',         label: 'FAQs',                icon: HelpCircle },
  { to: '/admin/contact',      label: 'Contact & Links',     icon: Mail },
  { to: '/admin/settings',     label: 'Site Settings',       icon: Settings },
];

export default function AdminDashboard() {
  const [checking, setChecking] = useState(true);
  const [authed, setAuthed] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    adminMe()
      .then(() => setAuthed(true))
      .catch(() => setAuthed(false))
      .finally(() => setChecking(false));
  }, []);

  if (checking) {
    return (
      <main className="min-h-screen grid place-items-center bg-brand-cream">
        <Loader2 className="w-6 h-6 animate-spin text-brand-ink" />
      </main>
    );
  }
  if (!authed) return <Navigate to="/admin/login" replace />;

  const handleLogout = async () => {
    try {
      await adminLogout();
    } catch (_) {
      /* ignore */
    }
    navigate('/admin/login', { replace: true });
  };

  return (
    <main className="min-h-screen bg-brand-cream flex">
      <aside className="w-64 shrink-0 bg-brand-ink text-brand-cream min-h-screen flex flex-col">
        <div className="px-5 py-6 border-b border-white/10">
          <div className="text-brand-sun text-xs font-semibold uppercase tracking-widest">
            Science Sorted
          </div>
          <div className="mt-1 font-display text-xl">CMS</div>
        </div>

        <nav className="flex-1 py-3 px-2 space-y-0.5" aria-label="Admin sections">
          {NAV.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={`nav-${to.split('/').pop()}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-xl text-sm ${
                  isActive
                    ? 'bg-brand-sun text-brand-ink font-semibold'
                    : 'text-brand-cream/85 hover:bg-white/5'
                }`
              }
            >
              <Icon className="w-4 h-4" aria-hidden="true" />
              {label}
            </NavLink>
          ))}
        </nav>

        <div className="p-3 border-t border-white/10 space-y-2">
          <a
            href="/"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="admin-view-website"
            className="flex items-center gap-2 px-3 py-2 rounded-xl text-sm bg-brand-sun text-brand-ink font-semibold hover:brightness-95"
          >
            <ExternalLink className="w-4 h-4" /> View website
          </a>
          <button
            type="button"
            onClick={handleLogout}
            data-testid="admin-logout"
            className="w-full flex items-center gap-2 px-3 py-2 rounded-xl text-sm text-brand-cream/85 hover:bg-white/5"
          >
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      </aside>

      <section className="flex-1 min-w-0 p-6 md:p-8 lg:p-10">
        <Routes>
          <Route index element={<Navigate to="enquiries" replace />} />
          <Route path="enquiries" element={<EnquiriesSection />} />
          <Route path="reviews"      element={<ReviewsSection />} />
          <Route path="testimonials" element={<WhatsAppTestimonialsSection />} />
          <Route path="courses"      element={<CoursesSection />} />
          <Route path="results"  element={<ResultsSection />} />
          <Route path="team"     element={<TeamSection />} />
          <Route path="faqs"     element={<FaqsSection />} />
          <Route path="contact"  element={<ContactSection />} />
          <Route path="settings" element={<SettingsSection />} />
        </Routes>
      </section>
    </main>
  );
}
